// app/PostHogPageView.tsx
"use client";

import { isPrivateCapabilityPath } from "@/lib/booking/sharedBooking";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { sanitizeVehicleSearchParams } from "@/lib/vehicle/corporateSearch";
import {
  hasAutoSaveAdAttribution,
  isFromAutoSaveAd,
  persistAutoSaveAdAttribution,
} from "@/lib/analytics/adAttribution";

export default function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  // Separate effect for tracking pageviews - runs on every pathname or searchParams change
  useEffect(() => {
    if (isPrivateCapabilityPath(pathname)) {
      posthog?.stopSessionRecording();
      return;
    }
    if (pathname && posthog) {
      let url = window.origin + pathname;
      const safeParams = sanitizeVehicleSearchParams(
        new URLSearchParams(searchParams.toString())
      );
      if (safeParams.size) url += `?${safeParams}`;
      posthog.capture("$pageview", {
        $current_url: url,
      });

      // AutoSave-ad attribution: when a visitor lands from an AutoSave ad, mark
      // them so the onboarding-autosave-experiment + webview-screen-variant
      // flags can target them. Done once per visitor (localStorage guard):
      //   - setPersonProperties ($set): durable on the PostHog person, so it
      //     survives the in-app-browser -> external-browser handoff in the
      //     onboarding webview flow (which adopts the originating distinct_id)
      //     and a hard reload, even after utm_campaign has dropped off the URL.
      //   - setPersonPropertiesForFlags: makes it available to flag evaluation
      //     immediately in this client session.
      // See lib/analytics/adAttribution.ts.
      if (isFromAutoSaveAd(searchParams) && !hasAutoSaveAdAttribution()) {
        persistAutoSaveAdAttribution();
        posthog.setPersonProperties({ from_autosave_ad: true });
        posthog.setPersonPropertiesForFlags({ from_autosave_ad: true });
      }
    }
  }, [pathname, searchParams, posthog]);

  return null;
}
